import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  Clock, 
  Car, 
  Award, 
  Zap, 
  ShoppingCart, 
  Eye, 
  CheckCircle, 
  Star,
  ChevronLeft,
  ChevronRight,
  Heart,
  Share2
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { products } from '../data/products';

interface Product {
  id: string;
  name: string;
  arabicName: string;
  image: string;
  images?: string[];
  price: number;
  originalPrice?: number;
  description: string;
  features: string[];
  badge?: string;
  rating: number;
  reviews: number;
  isPopular?: boolean;
  isNew?: boolean;
}

interface ProductShowcaseProps {
  onOrderClick: (productId?: string) => void;
}

const ProductShowcase: React.FC<ProductShowcaseProps> = ({ onOrderClick }) => {
  const [selectedProduct, setSelectedProduct] = useState<string>('block-pedal');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [favorites, setFavorites] = useState<string[]>([]);

  const products: Product[] = [
    {
      id: 'block-pedal',
      name: 'Block Pedal',
      arabicName: 'بلوك دواسة',
      image: 'https://i.ibb.co/4ZmHmKJd/Whats-App-Image-2025-09-10-at-3-04-51-PM.jpg',
      images: [
        'https://i.ibb.co/4ZmHmKJd/Whats-App-Image-2025-09-10-at-3-04-51-PM.jpg',
        'https://i.ibb.co/Zp396GD2/Whats-App-Image-2025-09-12-at-15-30-53.jpg'
      ],
      price: 49.900,
      originalPrice: 59.900,
      description: 'بلوك بيدال يحمي كرهبتك بأقوى جودة!',
      features: ['تركيب في دقيقتين', 'متوافق مع جميع السيارات', 'مقاوم للكسر', 'ضمان سنة كاملة'],
      badge: 'الأكثر مبيعاً',
      rating: 4.9,
      reviews: 1247,
      isPopular: true
    },
    {
      id: 'cadenas-alarm',
      name: 'Cadenas Alarm',
      arabicName: 'قفل إنذار',
      image: 'https://i.ibb.co/whNMSK8j/image-11.png',
      price: 30.000,
      description: 'قفل ذكي مع نظام إنذار صوتي قوي. حماية إضافية لسيارتك.',
      features: ['إنذار صوتي 120 ديسيبل', 'مقاوم للماء', 'بطارية طويلة المدى', 'سهل الاستخدام'],
      badge: 'جديد',
      rating: 4.7,
      reviews: 623,
      isNew: true
    },
    {
      id: 'block-pedal-2x1',
      name: 'Block Pedal 2x1',
      arabicName: 'بلوك دواسة 2×1',
      image: 'https://i.ibb.co/4ZmHmKJd/Whats-App-Image-2025-09-10-at-3-04-51-PM.jpg',
      price: 99.900,
      originalPrice: 139.800,
      description: 'عرض خاص - احصل على جهازين بسعر مميز. حماية مضاعفة لسيارتك.',
      features: ['جهازين في عبوة واحدة', 'توفير 40 دينار', 'ضمان سنة كاملة', 'شحن مجاني'],
      badge: 'عرض خاص',
      rating: 4.9,
      reviews: 856,
      isPopular: true
    }
  ];

  useEffect(() => {
    setIsVisible(true);
    const timer = setInterval(() => {
      if (selectedProduct) {
        const product = products.find(p => p.id === selectedProduct);
        if (product?.images && product.images.length > 1) {
          setCurrentImageIndex(prev => (prev + 1) % product.images!.length);
        }
      }
    }, 3000);
    return () => clearInterval(timer);
  }, [selectedProduct, products]);

  const selectedProductData = products.find(p => p.id === selectedProduct);

  const toggleFavorite = (productId: string) => {
    setFavorites(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const shareProduct = (product: Product) => {
    if (navigator.share) {
      navigator.share({
        title: `${product.name} - Totale Sécurité`,
        text: product.description,
        url: window.location.href
      });
    }
  };

  const nextImage = () => {
    if (selectedProductData?.images) {
      setCurrentImageIndex(prev => (prev + 1) % selectedProductData.images!.length);
    }
  };

  const prevImage = () => {
    if (selectedProductData?.images) {
      setCurrentImageIndex(prev => 
        prev === 0 ? selectedProductData.images!.length - 1 : prev - 1
      );
    }
  };

  const generalFeatures = [
    { icon: <Shield className="w-6 h-6" />, title: 'حماية فائقة', desc: 'تقنية متطورة', color: 'text-blue-600 bg-blue-100' },
    { icon: <Clock className="w-6 h-6" />, title: 'تركيب سريع', desc: '2 دقائق فقط', color: 'text-green-600 bg-green-100' },
    { icon: <Car className="w-6 h-6" />, title: 'متوافق عالمياً', desc: 'جميع أنواع السيارات', color: 'text-purple-600 bg-purple-100' },
    { icon: <Award className="w-6 h-6" />, title: 'جودة عالية', desc: 'مواد مقاومة', color: 'text-orange-600 bg-orange-100' },
    { icon: <Zap className="w-6 h-6" />, title: 'فعالية مؤكدة', desc: '99.9% نجاح', color: 'text-red-600 bg-red-100' }
  ];

  return (
    <section id="products" className="py-16 lg:py-24 bg-gradient-to-br from-gray-50 via-white to-blue-50 relative overflow-hidden">
      
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-blue-200 rounded-full blur-3xl opacity-20 animate-pulse-slow"></div>
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-red-200 rounded-full blur-3xl opacity-20 animate-pulse-slow"></div>
      </div>

      <div className="container mx-auto px-4 lg:px-6 relative z-10">
        
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className={`${isVisible ? 'animate-fade-in' : 'opacity-0'}`}>
            <div className="inline-flex items-center space-x-2 bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Shield className="w-4 h-4" />
              <span>منتجاتنا المميزة</span>
            </div>
            
            <h2 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-800 mb-6">
              <span className="gradient-text">أجهزة الحماية</span>
              <span className="block text-gray-800">الأكثر تطوراً</span>
            </h2>
            
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              اكتشف مجموعتنا المتنوعة من أجهزة مكافحة السرقة المصممة لحماية سيارتك بأحدث التقنيات
            </p>
          </div>
        </div>

        {/* Main Product Display */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-20">
          
          {/* Product Image Section */}
          <div className={`relative ${isVisible ? 'animate-slide-up' : 'opacity-0'}`}>
            <div className="relative bg-white rounded-3xl p-8 shadow-2xl">
              
              {/* Product Image */}
              <div className="relative aspect-square mb-6 overflow-hidden rounded-2xl bg-gray-50">
                {selectedProductData?.images && selectedProductData.images.length > 1 ? (
                  <>
                    <img 
                      src={selectedProductData.images[currentImageIndex]}
                      alt={selectedProductData.arabicName}
                      className="w-full h-full object-cover transition-all duration-500 animate-float"
                    />
                    
                    {/* Navigation Arrows */}
                    <button 
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-all duration-300"
                    >
                      <ChevronLeft className="w-5 h-5 text-gray-700" />
                    </button>
                    
                    <button 
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-all duration-300"
                    >
                      <ChevronRight className="w-5 h-5 text-gray-700" />
                    </button>

                    {/* Image Indicators */}
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                      {selectedProductData.images.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentImageIndex(index)}
                          className={`w-2 h-2 rounded-full transition-all duration-300 ${
                            currentImageIndex === index ? 'bg-white w-6' : 'bg-white/50'
                          }`}
                        />
                      ))}
                    </div>
                  </>
                ) : (
                  <img 
                    src={selectedProductData?.image}
                    alt={selectedProductData?.arabicName}
                    className="w-full h-full object-cover animate-float"
                  />
                )}
              </div>

              {/* Product Actions */}
              <div className="flex justify-between items-center">
                <div className="flex space-x-3">
                  <button 
                    onClick={() => selectedProductData && toggleFavorite(selectedProductData.id)}
                    className="w-12 h-12 bg-gray-100 hover:bg-red-100 rounded-full flex items-center justify-center transition-all duration-300 group"
                  >
                    <Heart className={`w-5 h-5 transition-all duration-300 ${
                      selectedProductData && favorites.includes(selectedProductData.id)
                        ? 'text-red-500 fill-current scale-110'
                        : 'text-gray-500 group-hover:text-red-500'
                    }`} />
                  </button>
                  
                  <button 
                    onClick={() => selectedProductData && shareProduct(selectedProductData)}
                    className="w-12 h-12 bg-gray-100 hover:bg-blue-100 rounded-full flex items-center justify-center transition-all duration-300 group"
                  >
                    <Share2 className="w-5 h-5 text-gray-500 group-hover:text-blue-500 transition-colors duration-300" />
                  </button>
                </div>

                <button 
                  onClick={() => selectedProductData && onOrderClick(selectedProductData.id)}
                  className="btn-primary flex items-center space-x-2"
                >
                  <ShoppingCart className="w-5 h-5" />
                  <span>اشتري الآن</span>
                </button>
              </div>

              {/* Floating Badges */}
              {selectedProductData?.badge && (
                <div className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold shadow-lg">
                  {selectedProductData.badge}
                </div>
              )}
              
              {selectedProductData?.originalPrice && (
                <div className="absolute top-4 left-4 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold shadow-lg">
                  وفر {((selectedProductData.originalPrice - selectedProductData.price) / selectedProductData.originalPrice * 100).toFixed(0)}%
                </div>
              )}
            </div>
          </div>

          {/* Product Details Section */}
          <div className={`space-y-8 ${isVisible ? 'animate-fade-in' : 'opacity-0'}`}>
            {selectedProductData && (
              <>
                <div>
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="flex items-center space-x-1">
                      {[...Array(5)].map((_, i) => (
                        <Star 
                          key={i} 
                          className={`w-5 h-5 ${
                            i < Math.floor(selectedProductData.rating) 
                              ? 'text-yellow-400 fill-current' 
                              : 'text-gray-300'
                          }`} 
                        />
                      ))}
                      <span className="text-gray-600 text-sm mr-2">
                        {selectedProductData.rating} ({selectedProductData.reviews} تقييم)
                      </span>
                    </div>
                  </div>
                  
                  <h3 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-2">
                    {selectedProductData.arabicName}
                  </h3>
                  <h4 className="text-xl text-gray-600 mb-4">{selectedProductData.name}</h4>
                  
                  <p className="text-lg text-gray-600 leading-relaxed mb-6">
                    {selectedProductData.description}
                  </p>

                  {/* Price */}
                  <div className="flex items-center space-x-4 mb-8">
                    <span className="text-4xl font-bold text-red-600">
                      {selectedProductData.price.toFixed(3)} د.ت
                    </span>
                    {selectedProductData.originalPrice && (
                      <span className="text-2xl text-gray-400 line-through">
                        {selectedProductData.originalPrice.toFixed(3)} د.ت
                      </span>
                    )}
                  </div>

                  {/* Features */}
                  <div className="space-y-3">
                    <h5 className="text-lg font-semibold text-gray-800 mb-4">المميزات الرئيسية:</h5>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {selectedProductData.features.map((feature, index) => (
                        <div key={index} className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                          <span className="text-gray-700 font-medium">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* General Features Section */}
        <div className="text-center">
          <h3 className="text-3xl font-bold text-gray-800 mb-12">لماذا تختار منتجاتنا؟</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {generalFeatures.map((feature, index) => (
              <div
                key={index}
                className={`card-modern p-6 text-center group hover-lift ${isVisible ? 'animate-slide-up' : 'opacity-0'}`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className={`w-16 h-16 mx-auto mb-4 ${feature.color} rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                  {feature.icon}
                </div>
                <h4 className="text-lg font-bold text-gray-800 mb-2">{feature.title}</h4>
                <p className="text-gray-600 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductShowcase;